import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A search-as-you-type box: debounce, results, "nothing matched", "lookup
 * failed", and a request that a later keystroke invalidates.
 *
 * Collections.js had five of these written out by hand -- region and
 * vegetation species in the wizard, region and vegetation species again in the
 * detail modal, and taxon in step 4 -- each about forty lines, each with its
 * own debounce ref, its own sequence ref and its own five pieces of state.
 * They were not quite identical, and the differences were accidents rather
 * than decisions: one tested `val.length`, another `val.trim().length`; three
 * invalidated in-flight requests on a manual pick and two did not.
 *
 * WHY THE SEQUENCE NUMBER. Two requests are in flight; the first one is slower.
 * Without it the older answer lands last and replaces the newer one, so the
 * list shows results for a prefix of what is in the box -- and a person who
 * has already clicked a suggestion sees it replaced underneath them. Every
 * answer here carries the sequence it was asked at, and a stale one is
 * dropped.
 *
 *     const region = useAutocomplete({
 *       search: term => api.get(`/regions/search?q=${encodeURIComponent(term)}`)
 *         .then(res => res.data || []),
 *     });
 *
 * `search` is the caller's, so the endpoint and its parameters stay in the
 * component that knows them. It is called only with a term at least
 * `minLength` long, and its promise resolving to an array is the contract.
 */
export const AUTOCOMPLETE_DELAY_MS = 300;
export const AUTOCOMPLETE_MIN_LENGTH = 2;

export default function useAutocomplete({
  search,
  minLength = AUTOCOMPLETE_MIN_LENGTH,
  delay = AUTOCOMPLETE_DELAY_MS,
  onType,
  onChoose,
} = {}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [noMatch, setNoMatch] = useState(false);
  const [failed, setFailed] = useState(false);

  const timer = useRef(null);
  const sequence = useRef(0);
  // Read through refs so a caller may pass an inline arrow without every
  // keystroke rebuilding the handlers -- and without a stale closure either.
  const searchRef = useRef(search);
  searchRef.current = search;
  const onTypeRef = useRef(onType);
  onTypeRef.current = onType;
  const onChooseRef = useRef(onChoose);
  onChooseRef.current = onChoose;

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    sequence.current += 1;
  }, []);

  // A pending request must not land after the component has gone.
  useEffect(() => cancel, [cancel]);

  const clearResults = useCallback(() => {
    setSuggestions([]);
    setOpen(false);
    setNoMatch(false);
    setFailed(false);
  }, []);

  /** Typing. Takes the text, or the event a bare <input> hands it. */
  const onChange = useCallback((eventOrValue) => {
    const value = typeof eventOrValue === 'string'
      ? eventOrValue : eventOrValue?.target?.value ?? '';
    cancel();
    const asked = sequence.current;
    setQuery(value);
    setNoMatch(false);
    if (onTypeRef.current) onTypeRef.current(value);

    if (value.trim().length < minLength) {
      setSuggestions([]);
      setOpen(false);
      setFailed(false);
      return;
    }
    timer.current = setTimeout(() => {
      // Called here rather than inside a `Promise.resolve().then(...)`: that
      // defers it by a microtask, which is invisible in the browser and makes
      // "the timer fired, so the search ran" untestable. try/catch covers a
      // `search` that throws before it returns a promise.
      let lookup;
      try {
        lookup = Promise.resolve(searchRef.current(value.trim()));
      } catch (e) {
        lookup = Promise.reject(e);
      }
      lookup
        .then((results) => {
          if (asked !== sequence.current) return;   // a later keystroke won
          const found = results || [];
          setSuggestions(found);
          setOpen(found.length > 0);
          setNoMatch(found.length === 0);
          setFailed(false);
        })
        .catch(() => {
          if (asked !== sequence.current) return;
          setSuggestions([]);
          setOpen(false);
          setFailed(true);
        });
    }, delay);
  }, [cancel, delay, minLength]);

  /** Picking one. The caller says what that means; the box just closes. */
  const choose = useCallback((item, { text = '' } = {}) => {
    cancel();
    setQuery(text);
    clearResults();
    if (onChooseRef.current) onChooseRef.current(item);
  }, [cancel, clearResults]);

  /** Back to empty: a form opening, or a record being re-read. */
  const reset = useCallback((text = '') => {
    cancel();
    setQuery(text);
    clearResults();
  }, [cancel, clearResults]);

  const close = useCallback(() => setOpen(false), []);
  /** Re-open the list that is already there, on focus. */
  const show = useCallback(() => setOpen(true), []);
  /** Another lookup for the same field failed -- the wizard's map pin asks
   *  /regions/at, and a failure there is the same message to the same person
   *  as a failure of the box itself. */
  const fail = useCallback(() => { setFailed(true); setOpen(false); }, []);

  return {
    query, setQuery, suggestions, open, noMatch, failed,
    onChange, choose, reset, close, show, fail, cancel,
  };
}
